import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { SimpleChangesUtils } from '@smooved/core';
import { SelectionType, WizardService, WizardStepCardComponent } from '@smooved/ui';
import { take } from 'rxjs/operators';
import { mapQuestionValidator } from '../../constants/surveys.constants';
import { QuestionType } from '../../enums/question-type.enum';
import { Category } from '../../interfaces/meta/category';
import { PageInterview } from '../../interfaces/meta/page-interview';
import { Question } from '../../interfaces/meta/question';
import { QuestionContainer } from '../../interfaces/meta/question-container';
import { QuestionValidator } from '../../interfaces/meta/question-validator';
import { Selection } from '../../interfaces/meta/questions/selection';
import { SurveysSandbox } from '../../sandboxes/surveys.sandbox';
import { SurveyService } from '../../services/survey.service';
import { SurveyUtils } from '../../utils/survey.utils';

@Component({
    selector: 'app-page-interview-component',
    template: `
        <smvd-ui-wizard-step-card
            [header]="page?.title | translateLabel"
            [hasPrevious]="stepStart > 1"
            [hasNext]="true"
            bodyClass="u-margin-top-none"
            (next)="onSubmit()"
        >
            <ng-container card-header-top><ng-content select="[page-interview-header]"></ng-content></ng-container>

            @if (page?.info; as info) {
                <ng-container card-header-info>
                    <div class="u-color-secondary u-padding-left-half" appTooltip [template]="infoBox" [inlineSVG]="'https://assets.smooved.be/icons/v2/16/question.svg'"></div>
                    <ng-template #infoBox>
                        <h6 *ngIf="info.title" class="u-margin-bottom-half">{{ info.title | translateLabel }}</h6>
                        <p *ngIf="info.description">{{ info.description | translateLabel }}</p>
                    </ng-template>
                </ng-container>
            }

            <app-meta-info class="u-display-block u-margin-bottom-triple" [move]="move" [interviewee]="interviewee"></app-meta-info>
            <form [formGroup]="form" class="u-flex-form u-flex-align-items-start u-container-small" *ngIf="this.form">
                <div>
                    <app-category-component
                        class="u-display-block u-w100p"
                        [ngClass]="{ 'u-margin-bottom-double u-bordered-bottom': !last }"
                        *ngFor="let category of page?.categories; let last = last"
                        [group]="form"
                        [category]="category"
                    ></app-category-component>
                </div>
            </form>
        </smvd-ui-wizard-step-card>
    `,
    styleUrls: ['page-interview.component.scss'],
})
export class PageInterviewComponent implements OnChanges, AfterViewInit {
    @ViewChild(WizardStepCardComponent) public wizardStep: WizardStepCardComponent;
    @Input() public page: PageInterview;
    @Input() public stepStart: number;
    @Input() public stepEnd: number;
    @Input() public interviewee: string;
    @Input() public move: any;

    @Output() public previous: EventEmitter<void> = new EventEmitter<void>();
    @Output() public next: EventEmitter<void> = new EventEmitter<void>();

    public form: UntypedFormGroup;

    constructor(
        public readonly translateService: TranslateService,
        private readonly surveySandbox: SurveysSandbox,
        private readonly surveyService: SurveyService,
        private readonly formBuilder: UntypedFormBuilder,
        private readonly wizardService: WizardService,
        private readonly cdr: ChangeDetectorRef
    ) {}

    public ngOnChanges({ page }: SimpleChanges): void {
        if (SimpleChangesUtils.hasChanged(page) && this.page) {
            this.wizardService.setCurrentStep(this.page.id);
            if (this.wizardStep) this.wizardStep.current = this.wizardService.getCurrentStepperIndex() + 1;
            this.buildInterviewForm(this.page);
        }
    }

    public ngAfterViewInit(): void {
        this.wizardStep.current = this.wizardService.getCurrentStepperIndex() + 1;
        this.cdr.detectChanges();
    }

    public onSubmit(): void {
        this.form.markAllAsTouched();
        if (!this.form.valid) return;
        this.surveySandbox.patch(this.form.value);
        this.surveyService
            .beforeSurveyNextPage(this.form.value)
            .pipe(take(1))
            .subscribe((goNext: boolean) => {
                if (goNext) this.next.emit();
            });
    }

    private buildInterviewForm(page: PageInterview): void {
        this.form = this.formBuilder.group({});

        page?.categories?.forEach((category): void => {
            category.questions?.forEach((question): void => {
                if (SurveyUtils.isQuestionContainer(question)) {
                    (question as QuestionContainer).questions?.forEach((q) => this.createFormControl(page, category, q));
                    return;
                }
                this.createFormControl(page, category, question as Question);
            });
        });

        this.surveySandbox
            .getAnswers()
            .pipe(take(1))
            .subscribe((answers) => {
                if (answers) this.form.patchValue(answers);
            });
    }

    private setValidators(validators: QuestionValidator[]): ValidatorFn[] {
        return validators?.map((validator) => mapQuestionValidator[validator.type](validator.value)) || [];
    }

    private createFormControl(page: PageInterview, category: Category, question: Question): void {
        const defaultFormGroupFactor = (): UntypedFormGroup => {
            return this.formBuilder.group({
                value: [null, this.setValidators(question.validators)],
                page: [page.id, []],
                category: [category.id, []],
            });
        };

        switch (question.type) {
            case QuestionType.Selection:
                switch ((question as Selection).selectionType) {
                    case SelectionType.Checkbox: {
                        const checkGroup = this.formBuilder.group({});
                        (question as Selection).options?.forEach((option): void => {
                            checkGroup.addControl(option.value, new UntypedFormControl(null));
                        });
                        this.form.addControl(
                            question.id,
                            this.formBuilder.group({
                                page: [page.id, []],
                                category: [category.id, []],
                                other: checkGroup,
                            })
                        );
                        break;
                    }
                    case SelectionType.Radio: {
                        const radioGroup = defaultFormGroupFactor();

                        if (this.surveySandbox.hasChildren((question as Selection).options)) {
                            radioGroup.addControl(
                                'other',
                                this.formBuilder.group({
                                    detail: [null, this.setValidators(question.validators)],
                                })
                            );
                        }

                        this.form.addControl(question.id, radioGroup);
                        break;
                    }
                    default: {
                        this.form.addControl(question.id, defaultFormGroupFactor());
                    }
                }
                break;
            case QuestionType.ContactPerson: {
                this.form.addControl(
                    'location',
                    this.formBuilder.group({
                        value: [null, this.setValidators(question.validators)],
                        page: page.id,
                    })
                );
                this.form.addControl(
                    'linkedRealEstateAgents',
                    this.formBuilder.group({
                        value: [null, this.setValidators(question.validators)],
                        page: page.id,
                    })
                );
                break;
            }
            case QuestionType.Score: {
                const scoreGroup = defaultFormGroupFactor();
                this.form.addControl(question.id, scoreGroup);
                this.surveySandbox.survey$.pipe(take(1)).subscribe(({ presetScore }) => scoreGroup.controls.value.setValue(presetScore));
                break;
            }
            default: {
                this.form.addControl(question.id, defaultFormGroupFactor());
            }
        }
    }
}
